/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TourData {
    id: string;
    releaseNotes: string;
    releaseDate: string;
}

interface TourState {
    versions: TourData[];
    loadingScript: boolean;
    loadingVersions: boolean;
    error: string | null;
    currentVersion: string | null;
}

const initialState: TourState = {
    loadingScript: false,
    loadingVersions: false,
    versions: [],
    error: null,
    currentVersion: null,
};

const tourSlice = createSlice({
    name: 'tour',
    initialState,
    reducers: {
        loadTourScript(state) {
            state.loadingScript = true;
            state.error = null;
        },
        fetchTourVersions(state) {
            state.loadingVersions = true;
            state.error = null;
        },
        fetchTourVersionsSuccess(
            state,
            action: PayloadAction<{ versions: TourData[] }>
        ) {
            state.loadingVersions = false;
            state.versions = action.payload.versions;
        },
        fetchTourVersionsFailed(
            state,
            action: PayloadAction<{ error: string }>
        ) {
            state.loadingVersions = false;
            state.error = action.payload.error;
        },
        startTour(
            state,
            action: PayloadAction<
                { version?: string; forceStart?: boolean } | undefined
            >
        ) {
            state.currentVersion = action.payload?.version ?? null;
            state.error = null;
        },
        startTourFailed(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
        },
        stopTour(state) {
            state.loadingVersions = false;
            state.versions = [];
        },

        startWalkThrough(
            state,
            action: PayloadAction<{ forceStart?: boolean } | undefined>
        ) {
            if (action.payload?.forceStart) {
                state.error = null;
            }
        },
        startWalkThroughFailed(
            state,
            action: PayloadAction<{ error: string }>
        ) {
            state.error = action.payload.error;
        },
    },
});

export const {
    loadTourScript,
    fetchTourVersions,
    fetchTourVersionsSuccess,
    fetchTourVersionsFailed,
    startTour,
    startTourFailed,
    stopTour,
    startWalkThrough,
    startWalkThroughFailed,
} = tourSlice.actions;

export const tourReducer = tourSlice.reducer;
